// Core API
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  Company,
  ExchangeRateRow,
  MonthlyExchangeRates,
  Site,
  Warehouse,
  Location,
  CompanyGroup,
  CompanyGroupMembersResponse,
  HierarchyTreeResponse,
  Role,
  UserProfile,
  UserManagementLog,
  UserSitePermission,
  CustomerSupplierRelationship,
  DashboardStats,
  CompanyRegistrationData,
  ViewContext,
  ViewContextUpdate,
  QueryParams,
  ActionResponse,
  LegalContent,
  LegalAcceptRequest,
  LegalAcceptResponse,
  SiteEmailSettings,
  SiteEmailSettingsUpdate,
  CustomerEmailLimit,
} from './types';

export const coreAPI = {
  // Dashboard
  // CR-3396: Accept view context params for hierarchical filtering
  getDashboardStats: (params?: Record<string, string | number>): Promise<AxiosResponse<DashboardStats>> =>
    api.get('/core/dashboard/', { params }),

  // Companies
  getCompanies: (params?: QueryParams): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/', { params }),

  getCompany: (id: number): Promise<AxiosResponse<Company>> =>
    api.get(`/core/companies/${id}/`),

  // CR-3396: Get companies accessible to the current user (own company + group siblings).
  // CR-4887: pass { context: 'supplier_filter' } from the Dashboard / Customer Performance
  // filter panels so customer-portal users get the SUPPLIER hierarchy they serve instead
  // of their own group siblings.
  getAccessibleCompanies: (params?: { context?: string; type?: 'supplier' | 'customer' }): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/accessible/', { params }),

  // CR-4669: Companies in the current user's group — read-only index used by
  // the Customer Profile's Group Structure tab. The main /core/companies/
  // list is gated by IsHelixUser, so customer users get 403; this action is
  // open to any authenticated user but scoped server-side to their own group.
  getMyGroupMembers: (params?: { company_type?: string; group?: number }): Promise<AxiosResponse<{ count: number; results: Company[] }>> =>
    api.get('/core/companies/my_group_members/', { params }),

  // Customer-Site options for the Dashboard filter dropdown. Portal-aware:
  // supplier portal returns customers via CustomerSupplierRelationship,
  // customer portal returns the user's own group siblings (their customer
  // sites), helix returns all active customers.
  getCustomerSitesForFilter: (params?: { linked?: boolean }): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/for-filter/', { params }),

  // The current customer's suppliers (Poeton sites they trade with). Used by
  // the customer dashboard's filter dropdown — their counterparty is the
  // supplier, not their own company.
  getMySuppliers: (): Promise<AxiosResponse<{ count: number; results: Company[] }>> =>
    api.get('/core/my-suppliers/'),

  createCompany: (data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.post('/core/companies/', data),

  updateCompany: (id: number, data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.put(`/core/companies/${id}/`, data),

  patchCompany: (id: number, data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.patch(`/core/companies/${id}/`, data),

  deleteCompany: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/companies/${id}/`),

  uploadCompanyLogo: (id: number, file: File): Promise<AxiosResponse<Company>> => {
    const form = new FormData();
    form.append('logo', file);
    return api.post(`/core/companies/${id}/upload-logo/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getCompanySites: (id: number): Promise<AxiosResponse<Site[]>> =>
    api.get(`/core/companies/${id}/sites/`),

  getCompanyUsers: (id: number): Promise<AxiosResponse<UserProfile[]>> =>
    api.get(`/core/companies/${id}/users/`),

  approveCompanyUsers: (id: number, userIds: number[]): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/companies/${id}/approve_users/`, { user_ids: userIds }),

  // Sites
  getSites: (params?: QueryParams): Promise<AxiosResponse<{ results: Site[] }>> =>
    api.get('/core/sites/', { params }),

  // CR-3396: Get sites accessible to the current user.
  // CR-4887: pass { context: 'supplier_filter' } from the Dashboard / Customer Performance
  // filter panels so customer-portal users get the SUPPLIER sites they serve instead
  // of their own authorised sites (the user-menu site switcher must NOT pass it).
  getAccessibleSites: (params?: { context?: string }): Promise<AxiosResponse<Site[]>> =>
    api.get('/core/sites/accessible/', { params }),

  getSite: (id: number): Promise<AxiosResponse<Site>> =>
    api.get(`/core/sites/${id}/`),

  createSite: (data: Partial<Site>): Promise<AxiosResponse<Site>> =>
    api.post('/core/sites/', data),

  updateSite: (id: number, data: Partial<Site>): Promise<AxiosResponse<Site>> =>
    api.put(`/core/sites/${id}/`, data),

  deleteSite: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/sites/${id}/`),

  // Warehouses
  getWarehouses: (params?: QueryParams): Promise<AxiosResponse<{ results: Warehouse[] }>> =>
    api.get('/core/warehouses/', { params }),

  // CR-4339: Get warehouses accessible to the current user
  getAccessibleWarehouses: (): Promise<AxiosResponse<Warehouse[]>> =>
    api.get('/core/warehouses/accessible/'),

  getWarehouse: (id: number): Promise<AxiosResponse<Warehouse>> =>
    api.get(`/core/warehouses/${id}/`),

  createWarehouse: (data: Partial<Warehouse>): Promise<AxiosResponse<Warehouse>> =>
    api.post('/core/warehouses/', data),

  updateWarehouse: (id: number, data: Partial<Warehouse>): Promise<AxiosResponse<Warehouse>> =>
    api.put(`/core/warehouses/${id}/`, data),

  deleteWarehouse: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/warehouses/${id}/`),

  // Locations
  getLocations: (params?: QueryParams): Promise<AxiosResponse<{ results: Location[] }>> =>
    api.get('/core/locations/', { params }),

  getLocation: (id: number): Promise<AxiosResponse<Location>> =>
    api.get(`/core/locations/${id}/`),

  createLocation: (data: Partial<Location>): Promise<AxiosResponse<Location>> =>
    api.post('/core/locations/', data),

  updateLocation: (id: number, data: Partial<Location>): Promise<AxiosResponse<Location>> =>
    api.put(`/core/locations/${id}/`, data),

  deleteLocation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/locations/${id}/`),

  // Company Groups
  getCompanyGroups: (params?: QueryParams): Promise<AxiosResponse<{ results: CompanyGroup[] }>> =>
    api.get('/core/company-groups/', { params }),

  getCompanyGroup: (id: number): Promise<AxiosResponse<CompanyGroup>> =>
    api.get(`/core/company-groups/${id}/`),

  // CR-3396: Get the company group accessible to the current user
  getAccessibleCompanyGroup: (): Promise<AxiosResponse<CompanyGroup | null>> =>
    api.get('/core/company-groups/accessible/'),

  // Customer-Group options for the Dashboard / Customer-Performance filter
  // dropdown. Pass { context: 'supplier_filter' } for customer-portal users
  // to receive supplier-side groups (mirrors CR-4887 on companies/sites).
  getCustomerGroupsForFilter: (
    params?: { context?: 'supplier_filter' }
  ): Promise<AxiosResponse<{ results: CompanyGroup[] }>> =>
    api.get('/core/company-groups/for-filter/', { params }),

  createCompanyGroup: (data: Partial<CompanyGroup>): Promise<AxiosResponse<CompanyGroup>> =>
    api.post('/core/company-groups/', data),

  updateCompanyGroup: (id: number, data: Partial<CompanyGroup>): Promise<AxiosResponse<CompanyGroup>> =>
    api.put(`/core/company-groups/${id}/`, data),

  deleteCompanyGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/company-groups/${id}/`),

  updateCompanyGroupPreferences: (id: number, data: Record<string, string | null>): Promise<AxiosResponse<Record<string, string | null>>> =>
    api.patch(`/core/company-groups/${id}/preferences/`, data),

  // CR-4579: Get member companies and aggregate stats for a group
  getCompanyGroupMembers: (id: number): Promise<AxiosResponse<CompanyGroupMembersResponse>> =>
    api.get(`/core/company-groups/${id}/members/`),

  // CR-5311: Get users across all member companies of a group
  getCompanyGroupUsers: (id: number): Promise<AxiosResponse<{ count: number; results: UserProfile[] }>> =>
    api.get(`/core/company-groups/${id}/users/`),

  // Hierarchy Tree
  getHierarchyTree: (params?: { include_inactive?: boolean; company_type?: 'supplier' | 'customer' }): Promise<AxiosResponse<HierarchyTreeResponse>> =>
    api.get('/core/hierarchy/', { params }),

  // Roles
  getRoles: (params?: QueryParams): Promise<AxiosResponse<{ results: Role[]; count: number }>> =>
    api.get('/core/roles/', { params }),

  getRole: (id: number): Promise<AxiosResponse<Role>> =>
    api.get(`/core/roles/${id}/`),

  // User Profiles
  getUserProfiles: (params?: QueryParams): Promise<AxiosResponse<{ results: UserProfile[]; count: number; next: string | null }>> =>
    api.get('/core/user-profiles/', { params }),

  getUserProfile: (id: number): Promise<AxiosResponse<UserProfile>> =>
    api.get(`/core/user-profiles/${id}/`),

  getMyProfile: (): Promise<AxiosResponse<UserProfile>> =>
    api.get('/core/user-profiles/me/'),

  // Read-only directory of the people at the viewer's own company (and any
  // sibling sites in the same group). Any authenticated user can call it —
  // suppliers see their org's users, customers see theirs. View only.
  getCompanyDirectory: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: UserProfile[] }>> =>
    api.get('/core/company-users/', { params }),

  approveUser: (id: number): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/user-profiles/${id}/approve/`),

  rejectUser: (id: number): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/user-profiles/${id}/reject/`),

  // Utility endpoints
  getPendingApprovals: (): Promise<AxiosResponse<{ count: number; users: UserProfile[] }>> =>
    api.get('/core/pending-approvals/'),

  getRecentRegistrations: (days?: number): Promise<AxiosResponse<{ count: number; companies: Company[] }>> =>
    api.get('/core/recent-registrations/', { params: { days } }),

  bulkApproveUsers: (userIds: number[]): Promise<AxiosResponse<ActionResponse>> =>
    api.post('/core/bulk-approve-users/', { user_ids: userIds }),

  // User Management Actions (for admins with can_manage_users permission)
  toggleUserActive: (id: number): Promise<AxiosResponse<{ status: string; is_active: boolean; message: string }>> =>
    api.post(`/core/user-profiles/${id}/toggle_active/`),

  // CR-5312: lifecycle delete. Backend hard-deletes when there's no
  // login / activity / management history; otherwise it deactivates the
  // user so the audit trail is preserved. Response carries `deleted` and
  // `deactivated` so the caller can render the right toast.
  safeDeleteUser: (id: number): Promise<AxiosResponse<{
    deleted: boolean;
    deactivated: boolean;
    message: string;
    is_active?: boolean;
    user?: UserProfile;
  }>> =>
    api.post(`/core/user-profiles/${id}/safe_delete/`),

  changeUserRole: (id: number, roleId: number): Promise<AxiosResponse<{ message: string; user: UserProfile }>> =>
    api.post(`/core/user-profiles/${id}/change_role/`, { role_id: roleId }),

  toggleUserAdmin: (id: number, isAdmin: boolean): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/core/user-profiles/${id}/toggle_admin/`, { is_admin: isAdmin }),

  // Master notification switches for a user (admin-managed). Both default OFF.
  getUserNotificationSettings: (
    id: number
  ): Promise<AxiosResponse<{ in_app_enabled: boolean; email_enabled: boolean }>> =>
    api.get(`/core/user-profiles/${id}/notification_settings/`),

  setUserNotificationSettings: (
    id: number,
    payload: { in_app_enabled?: boolean; email_enabled?: boolean }
  ): Promise<AxiosResponse<{ in_app_enabled: boolean; email_enabled: boolean }>> =>
    api.patch(`/core/user-profiles/${id}/notification_settings/`, payload),

  sendInvitationEmail: (
    profileId: number
  ): Promise<AxiosResponse<{ message: string; user?: UserProfile }>> =>
    api.post(`/core/user-profiles/${profileId}/send_invitation/`),

  // CR-5857: admin edits a managed user's identity / contact / role fields.
  // Only the keys present in `data` are changed; omit a field to leave it as-is.
  updateUserProfileFields: (
    id: number,
    data: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      job_title?: string;
      department?: string;
      role?: number | null;
    }
  ): Promise<AxiosResponse<{ message: string; user: UserProfile }>> =>
    api.patch(`/core/user-profiles/${id}/update_profile/`, data),

  // CR-5857: admin triggers a branded password-reset email for a managed user
  // without needing the user's inbox to start the flow.
  adminResetPassword: (
    id: number
  ): Promise<AxiosResponse<{ message: string; email: string }>> =>
    api.post(`/core/user-profiles/${id}/admin_reset_password/`),

  // Set a temporary password directly and return it, so an admin can unblock a
  // user who can't use the email reset/invite link. `force_change` (default
  // off) opts the user into a required password change on first login.
  adminSetTempPassword: (
    id: number,
    opts?: { password?: string; force_change?: boolean },
  ): Promise<AxiosResponse<{ message: string; email: string; temp_password: string; force_change: boolean }>> =>
    api.post(`/core/user-profiles/${id}/admin_set_temp_password/`, {
      ...(opts?.password ? { password: opts.password } : {}),
      force_change: Boolean(opts?.force_change),
    }),

  getUserManagementLogs: (id: number): Promise<AxiosResponse<UserManagementLog[]>> =>
    api.get(`/core/user-profiles/${id}/management_logs/`),

  getAvailableRoles: (): Promise<AxiosResponse<{ roles: Role[] }>> =>
    api.get('/core/user-profiles/available_roles/'),

  // Site Permissions
  // CR-5344: Optional companyId scopes the returned site list to that
  // company's group instead of the user's home company group. Use this
  // when opening the popup from a specific customer's context to avoid
  // leaking the user's other-tenant site memberships.
  getUserSitePermissions: (
    userId: number,
    companyId?: number
  ): Promise<AxiosResponse<UserSitePermission[]>> =>
    api.get(`/core/user-profiles/${userId}/site_permissions/`, {
      params: companyId ? { scope_company: companyId } : undefined,
    }),

  updateUserSitePermission: (
    userId: number,
    siteId: number,
    data: Partial<UserSitePermission>
  ): Promise<AxiosResponse<UserSitePermission>> =>
    api.post(`/core/user-profiles/${userId}/site_permissions/`, { site_id: siteId, ...data }),

  deleteUserSitePermission: (userId: number, siteId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/core/user-profiles/${userId}/site_permissions/${siteId}/`),

  // Registration
  registerCompany: (data: CompanyRegistrationData): Promise<AxiosResponse<{ message: string; company: Company }>> =>
    api.post('/core/register/company/', data),

  getRegistrationInfo: (): Promise<AxiosResponse<{
    company_types: Array<{ value: string; label: string }>;
    site_types: Array<{ value: string; label: string }>;
    warehouse_types: Array<{ value: string; label: string }>;
  }>> =>
    api.get('/core/registration-info/'),

  getCompanyTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/core/company-types/'),

  // View Context - Database-backed view switching (replaces localStorage)
  getViewContext: (): Promise<AxiosResponse<ViewContext>> =>
    api.get('/core/view-context/'),

  updateViewContext: (data: Partial<ViewContextUpdate>): Promise<AxiosResponse<ViewContext>> =>
    api.patch('/core/view-context/', data),

  // Exchange Rates - Currency conversion
  /** Paginated rate history. Admin-only on the backend. */
  getExchangeRates: (params?: {
    currency?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<PaginatedResponse<ExchangeRateRow>>> =>
    api.get('/core/exchange-rates/', { params }),

  /** One rate per currency per calendar month (the month's last stored rate). */
  getMonthlyExchangeRates: (params?: {
    currency?: string;
    months?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<AxiosResponse<MonthlyExchangeRates>> =>
    api.get('/core/exchange-rates/monthly/', { params }),

  getLatestExchangeRates: (): Promise<AxiosResponse<{
    base_currency: string;
    rates: Record<string, string>;
    as_of: string;
  }>> =>
    api.get('/core/exchange-rates/latest/'),

  getExchangeRatesForDate: (date: string): Promise<AxiosResponse<{
    base_currency: string;
    requested_date: string;
    rates: Record<string, string>;
  }>> =>
    api.get('/core/exchange-rates/for_date/', { params: { date } }),

  convertCurrency: (data: {
    amount: number;
    from_currency: string;
    to_currency: string;
    date?: string;
  }): Promise<AxiosResponse<{
    amount: string;
    from_currency: string;
    to_currency: string;
    converted_amount: string;
    exchange_rate: string;
    date: string;
  }>> =>
    api.post('/core/exchange-rates/convert/', data),

  // Version & Changelog
  getVersion: (): Promise<AxiosResponse<{
    version: string;
    build_date: string;
    build_number: number;
    git_commit: string;
    release_notes: string;
    server_time: string;
  }>> =>
    api.get('/core/version/'),

  getChangelog: (): Promise<AxiosResponse<{ changelog: string }>> =>
    api.get('/core/changelog/'),

  getAppInfo: (): Promise<AxiosResponse<{
    version: string;
    build_date: string;
    build_number: number;
    git_commit: string;
    release_notes: string;
    server_time: string;
    changelog: string;
  }>> =>
    api.get('/core/app-info/'),

  // Legal Content
  getLegalContent: (): Promise<AxiosResponse<LegalContent>> =>
    api.get('/core/legal/'),

  acceptLegal: (data: LegalAcceptRequest): Promise<AxiosResponse<LegalAcceptResponse>> =>
    api.post('/core/legal/accept/', data),

  // Customer-Supplier Relationships
  getCustomerRelationships: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerSupplierRelationship[] }>> =>
    api.get('/core/customer-relationships/', { params }),

  updateCustomerRelationship: (id: number, data: Partial<CustomerSupplierRelationship>): Promise<AxiosResponse<CustomerSupplierRelationship>> =>
    api.patch(`/core/customer-relationships/${id}/`, data),

  createCustomerRelationship: (data: { customer: number; supplier: number }): Promise<AxiosResponse<CustomerSupplierRelationship>> =>
    api.post('/core/customer-relationships/', data),

  // Intra-group site link: make one operating-org site visible as a supplier
  // of another. The backend resolves (or creates) the buying site's
  // customer-typed twin company and ensures an active
  // CustomerSupplierRelationship(supplier=site, customer=twin). Idempotent;
  // both_directions repeats with the roles swapped. Helix / operating-org
  // admins only.
  linkIntraSite: (data: {
    supplier_site_id: number;
    buyer_site_id: number;
    both_directions?: boolean;
    /** DNA/ERP AccountsCode stamped on the buyer's twin (how DNA frames resolve). */
    erp_code?: string;
    /** Code for the reverse direction's twin when both_directions is set. */
    reverse_erp_code?: string;
  }): Promise<AxiosResponse<{
    directions: Array<{
      supplier_site_id: number;
      supplier_site_name: string;
      buyer_site_id: number;
      buyer_site_name: string;
      twin_id: number;
      twin_name: string;
      twin_created: boolean;
      twin_linked: boolean;
      relationship_created: boolean;
      relationship_reactivated: boolean;
      erp_code: string;
      erp_code_set: boolean;
      erp_code_error: string | null;
    }>;
  }>> =>
    api.post('/core/companies/link-intra-site/', data),

  deleteCustomerRelationship: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/customer-relationships/${id}/`),

  /**
   * Per-site email + notification overrides.
   *
   * Returns one entry per supplier site the caller may configure, including
   * sites with no stored row (all fields null = inherits the platform value),
   * so the UI never has to distinguish "no row" from "inherits".
   */
  getSiteEmailSettings: (): Promise<AxiosResponse<SiteEmailSettings[]>> =>
    api.get('/core/site-email-settings/'),

  /**
   * Update one site's email overrides (upsert, keyed by SITE id).
   *
   * @param siteId - Company id of the supplier site.
   * @param data - Fields to change; send null to clear an override so the
   *   site falls back to the platform value.
   */
  updateSiteEmailSettings: (
    siteId: number,
    data: SiteEmailSettingsUpdate,
  ): Promise<AxiosResponse<SiteEmailSettings>> =>
    api.patch(`/core/site-email-settings/${siteId}/`, data),

  /** Customers that override the platform per-recipient daily email cap. */
  getCustomerEmailLimits: (): Promise<AxiosResponse<CustomerEmailLimit[]>> =>
    api.get('/core/customer-email-limits/'),

  /**
   * Set or clear one customer's daily email cap.
   *
   * @param companyId - Customer Company id.
   * @param cap - Custom cap, or null to fall back to the platform default.
   */
  updateCustomerEmailLimit: (
    companyId: number,
    cap: number | null,
  ): Promise<AxiosResponse<CustomerEmailLimit>> =>
    api.patch(`/core/customer-email-limits/${companyId}/`, {
      email_rate_limit_per_day: cap,
    }),
};
